/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment-timezone';
// eslint-disable-next-line import/no-extraneous-dependencies
import { RefreshCw } from 'lucide-react';
import TabPageHeader from './TabPageHeader';
import DataTable from '../../components/DataTable';
import RowActionMenu from '../../components/RowActionMenu';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import HolidayModal from './HolidayModal';
import DeleteModal from './DeleteModal';

const dash = <span className="text-ink-500">—</span>;

function HolidaysPanel() {
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [holidays, setHolidays] = useState([]);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddHolidayModal, setShowAddHolidayModal] = useState(false);
  const [showEditHolidayModal, setShowEditHolidayModal] = useState(false);
  const [showDeleteHolidayModal, setShowDeleteHolidayModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);

  const getHolidays = async () => {
    const res = await sendRequest({ method: 'GET', url: API.WORKSPACE.offTimeHolidays(activeWorkspace.id) });
    if (res.success) {
      const offTimeHolidays = res.data.ptoHolidays.map((holiday) => {
        if (holiday?.members?.length) {
          return { ...holiday, members: holiday.members.map((member) => ({ id: member.memberId, name: member.user.fullName })) };
        }
        return holiday;
      });
      setHolidays(offTimeHolidays);
    } else {
      displayNotification({ message: 'An error occurs while fetching PTO Holidays.', severity: 'error' });
    }
  };

  const getMembers = async () => {
    const res = await sendRequest({ method: 'get', url: API.WORKSPACE.members(activeWorkspace.id) });
    if (res.success) {
      const membersArr = res.data.members.map((member) => ({
        id: member.id,
        name: member?.user?.firstName && member?.user?.lastName ? `${member.user.firstName} ${member.user.lastName}` : member.user.email,
      }));
      setMembers(membersArr);
    } else {
      displayNotification({ message: 'An error occurs while fetching teams.', severity: 'error' });
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([getHolidays(), getMembers()]).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteHolidayPolicy = async () => {
    const { success } = await sendRequest({ method: 'delete', url: API.WORKSPACE.deleteHoliday(activeWorkspace.id, selectedHoliday.id) });
    if (success) {
      setShowDeleteHolidayModal(false);
      displayNotification({ message: 'Holiday has been deleted successfully.', severity: 'success' });
      getHolidays();
      getMembers();
    } else {
      displayNotification({ message: 'There was an issue deleting a holiday.', severity: 'error' });
    }
  };

  const columns = [
    { key: 'name', label: 'Holiday', sortable: true, width: 240, flex: true, render: (h) => <span className="font-medium text-ink-900">{h.name}</span> },
    { key: 'members', label: 'Members', sortable: true, width: 120, render: (h) => <span className="text-ink-700">{h.members?.length || 'None'}</span> },
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      width: 220,
      flex: true,
      render: (h) => (
        <span className="inline-flex items-center gap-1.5 text-ink-700 whitespace-nowrap">
          {h.occursAnnually ? <RefreshCw size={13} className="text-ink-500" title="Recurs annually" /> : null}
          {moment(h.date, 'YYYY-MM-DD').format('ddd, MMM D, YYYY')}
        </span>
      ),
    },
    { key: 'timezone', label: 'Timezone', width: 140, render: (h) => <span className="text-ink-700">{h.timezone || dash}</span> },
    {
      key: 'action',
      label: '',
      width: 72,
      align: 'center',
      render: (h) => (
        <RowActionMenu
          ariaLabel={`Actions for ${h.name}`}
          items={[
            { label: 'Edit Holiday', onClick: () => { setSelectedHoliday(h); setShowEditHolidayModal(true); } },
            { label: 'Delete Holiday', destructive: true, onClick: () => { setSelectedHoliday(h); setShowDeleteHolidayModal(true); } },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      {showAddHolidayModal && <HolidayModal closeModal={() => setShowAddHolidayModal(false)} members={members} getHolidays={getHolidays} />}
      {showEditHolidayModal && <HolidayModal closeModal={() => setShowEditHolidayModal(false)} members={members} getHolidays={getHolidays} selectedHoliday={selectedHoliday} />}
      {showDeleteHolidayModal && (
        <DeleteModal
          policyName={selectedHoliday.name}
          setShowModal={setShowDeleteHolidayModal}
          callback={() => deleteHolidayPolicy(selectedHoliday.id)}
          type="holiday"
          date={`which occurs on ${moment(selectedHoliday.date).utc().format('M/D/YY')}`}
        />
      )}
      <TabPageHeader title="Holidays" subtitle="Set up holidays for time off" buttonName="Add Holiday" onClickBtn={() => setShowAddHolidayModal(true)} />
      <DataTable bare columns={columns} rows={holidays} getRowKey={(h) => h.id} loading={isLoading} emptyText="No holidays yet." minWidth={820} />
    </>
  );
}

export default HolidaysPanel;
